(() => {
  const STORAGE_KEY = 'loubna_marketing_attribution_v1';
  const TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const nativeFetch = window.fetch.bind(window);

  const parseJson = (value) => {
    try { return JSON.parse(value || 'null'); } catch (_) { return null; }
  };

  const readCookie = (name) => {
    try {
      const prefix = name + '=';
      const part = document.cookie.split(';').map((x) => x.trim()).find((x) => x.startsWith(prefix));
      return part ? decodeURIComponent(part.slice(prefix.length)) : '';
    } catch (_) {
      return '';
    }
  };

  const validFbp = (value) => /^fb\.1\.\d{10,16}\.\d+$/.test(value || '');
  const validFbc = (value) => /^fb\.1\.\d{10,16}\.[A-Za-z0-9._-]+$/.test(value || '');

  const metaBrowserIds = (params, existing = {}) => {
    const cookieFbp = readCookie('_fbp');
    const cookieFbc = readCookie('_fbc');
    const fbclid = params.get('fbclid');

    const fbp = validFbp(cookieFbp) ? cookieFbp : (validFbp(existing.fbp) ? existing.fbp : null);

    let fbc = validFbc(cookieFbc) ? cookieFbc : (validFbc(existing.fbc) ? existing.fbc : null);

    if (!fbc && fbclid && /^[A-Za-z0-9._-]+$/.test(fbclid)) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
    }

    return { fbp, fbc };
  };

  const cleanReferrer = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value);
      return `${url.origin}${url.pathname}`;
    } catch (_) {
      return '';
    }
  };

  const readStored = () => {
    try {
      const stored = parseJson(localStorage.getItem(STORAGE_KEY));
      if (!stored) return null;
      const updatedAt = Date.parse(stored.updated_at || '');
      if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stored;
    } catch (_) {
      return null;
    }
  };

  const writeStored = (value) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (_) {}
    return value;
  };

  const capture = () => {
    const params = new URLSearchParams(location.search);
    const utm = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 250);
    });

    const now = new Date().toISOString();
    const landingPage = location.pathname;
    const referrer = cleanReferrer(document.referrer);
    const existing = readStored() || {};
    const metaIds = metaBrowserIds(params, existing);
    const firstTouch = existing.first_touch || {
      landing_page: landingPage,
      referrer,
      captured_at: now,
      ...utm,
    };
    const lastTouch = Object.keys(utm).length
      ? { landing_page: landingPage, referrer, captured_at: now, ...utm }
      : (existing.last_touch || firstTouch);

    return writeStored({
      first_touch: firstTouch,
      last_touch: lastTouch,
      fbp: metaIds.fbp || null,
      fbc: metaIds.fbc || null,
      updated_at: now,
    });
  };

  const getTracking = () => {
    const stored = readStored() || capture();
    return {
      first_touch: stored?.first_touch || null,
      last_touch: stored?.last_touch || null,
      fbp: stored?.fbp || null,
      fbc: stored?.fbc || null,
    };
  };

  const publish = (name, data) => {
    try {
      const result = window.Shopify?.analytics?.publish?.(name, data);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (_) {}
  };

  const isEndpoint = (input, endpoint) => {
    const url = typeof input === 'string' ? input : input?.url;
    return typeof url === 'string' && url.includes(`/functions/v1/${endpoint}`);
  };

  capture();
  window.LoubnaTracking = { capture, get: getTracking };

  window.fetch = async function trackedFetch(input, init) {
    const isCodOrder = isEndpoint(input, 'create-order');
    const isStripeSession = isEndpoint(input, 'create-stripe-checkout-session');

    if ((!isCodOrder && !isStripeSession) || !init || typeof init.body !== 'string') {
      return nativeFetch(input, init);
    }

    const payload = parseJson(init.body);
    if (!payload || typeof payload !== 'object') return nativeFetch(input, init);

    const tracking = getTracking();
    payload.tracking = tracking;
    const nextInit = { ...init, body: JSON.stringify(payload) };

    if (isCodOrder) {
      publish('loubna_store:cod_checkout_started', {
        value: payload.grand_total || payload.total_price || null,
        currency: 'DZD',
        total_quantity: payload.total_quantity || null,
        items: Array.isArray(payload.cart_items)
          ? payload.cart_items.map(({ variant_id, quantity }) => ({ variant_id, quantity }))
          : [],
        tracking,
      });
    }

    const response = await nativeFetch(input, nextInit);

    if (isCodOrder && response.ok) {
      response.clone().json().then((body) => {
        const serverTotals = body?.totals || {};
        publish('loubna_store:cod_order_created', {
          order_id: body?.order_id || null,
          value: serverTotals.grand_total ?? payload.grand_total ?? payload.total_price ?? null,
          currency: 'DZD',
          total_quantity: payload.total_quantity || null,
          items: Array.isArray(payload.cart_items)
            ? payload.cart_items.map(({ variant_id, quantity }) => ({ variant_id, quantity }))
            : [],
          tracking,
        });
      }).catch(() => {});
    }

    return response;
  };
})();
